<template>

  <div class="space-y-4 sm:space-y-6" ref="pageTop">
      <!-- Page Header -->
      <div class="border-b border-gray-200 pb-4 sm:pb-6">
        <h2 class="text-base sm:text-lg font-semibold text-gray-900">{{ t('wc_product_whatsapp_button') }}</h2>
        <p class="text-xs sm:text-sm text-gray-500 mt-1">{{ t('wc_product_description') }}</p>
      </div>
  
      <div class="flex flex-col lg:flex-row gap-6 lg:gap-8 justify-between">
  
        <!-- Left Column - Form -->
        <div class="flex-1 space-y-4 sm:space-y-6 max-w-full lg:max-w-lg">
  
          <!-- Enable Toggle -->
          <div class="flex items-center gap-3 cursor-pointer" @click="settings.enabled = !settings.enabled">
            <Toggle v-model="settings.enabled" @click.stop />
            <label class="text-sm font-medium text-gray-700 cursor-pointer">{{ t('wc_show_button_product') }}</label>
          </div>
  
          <template v-if="settings.enabled">
          <!-- WhatsApp Number -->
          <div class="space-y-2" ref="whatsappNumberField">
            <label class="text-xs sm:text-sm font-medium text-gray-700">{{ t('whatsapp_number') }}</label>
            <div class="flex items-center gap-2 sm:gap-3">
              <Select
                :options="countries"
                v-model="settings.country_code"
                :cursor="false"
                class="w-20 sm:w-24 justify-center text-center flex-shrink-0"
                :placeholder="t('select')"
              />
              <div class="w-full relative">
                <Text
                  ref="whatsappInput"
                  v-model="settings.whatsapp_number"
                  placeholder="1234567890"
                  :error="hasWhatsAppError"
                  @input="resetWhatsAppError"
                />
                <transition name="bounce">
                  <div v-if="hasWhatsAppError"
                    class="absolute w-4 text-red-500 right-3 top-3 flex items-center gap-2 transition"
                    :class="{ 'bounce-enter-active': hasWhatsAppError }">
                    <svg xmlns="http://www.w3.org/2000/svg" class="fill-current" viewBox="0 0 16 16">
                      <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                    </svg>
                  </div>
                </transition>
              </div>
            </div>
            <transition name="slide-fade">
              <p v-if="whatsappError" class="text-sm text-red-600">
                {{ t('valid_whatsapp_number_error') }}
              </p>
            </transition>
          </div>
  
          <!-- Button Position -->
          <div class="space-y-2">
            <label class="text-xs sm:text-sm font-medium text-gray-700">{{ t('button_position') }}</label>
            <Select
              v-model="settings.button_position"
              :options="positionOptions"
              @locked="$upgrade.show()"
            />
          </div>
  
          <!-- Button Text -->
          <div class="space-y-2">
            <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
              {{ t('button_text') }}
              <ProBadge />
            </label>
            <div :class="{ 'formychat-locked opacity-60 pointer-events-none': isLocked }">
              <Text
                v-model="settings.button_text"
                :placeholder="t('buy_on_whatsapp')"
              />
            </div>
          </div>
  
          <!-- Pre-filled Message -->
          <div class="space-y-2">
            <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
              {{ t('pre_filled_message') }}
              <Tooltip>{{ t('wc_message_tooltip') }}</Tooltip>
              <ProBadge />
            </label>
            <div :class="{ 'formychat-locked opacity-60 pointer-events-none': isLocked }">
              <MessagePreset
                v-model="settings.message_template"
                :fields="woocommerceFields"
                form="woocommerce"
                :defaultMessage="defaultMessageTemplate"
                :customTags="false"
              />
            </div>
          </div>
  
  
  
          <!-- Advanced Settings -->
          <transition name="slide-fade">
            <div v-if="showAdvanced" class="space-y-4 sm:space-y-6 mb-6 sm:mb-10">
  
              <!-- Button Style Section -->
              <div class="space-y-2">
                <div>
                  <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                    {{ t('button_style') }}
                    <ProBadge />
                  </label>
                  <p class="text-xs text-gray-500 mt-1">{{ t('wc_customize_button_appearance') }}</p>
                </div>
  
                <!-- Color Pickers Grid -->
                <div class="space-y-3 sm:space-y-4">
                  <div class="bg-gray-100 rounded-lg p-4 space-y-3 sm:space-y-4 relative" :class="{ 'formychat-locked opacity-60': isLocked }">
                    <!-- Reset Button -->
                    <button
                      v-if="hasColorChanges"
                      @click="confirmResetColors"
                      class="absolute top-3 right-3 text-xs px-2 py-1 border border-gray-300 rounded text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors bg-white"
                    >
                      {{ t('reset') }}
                    </button>
                    <!-- Background Color -->
                    <div class="flex items-center gap-2 sm:gap-3">
                      <label class="relative cursor-pointer flex-shrink-0">
                        <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-4 border-white shadow-md ring-1 ring-gray-200" :style="{background: settings.bg_color}"></div>
                        <input type="color" v-model="settings.bg_color" class="absolute w-0 h-0 opacity-0" />
                      </label>
                      <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                        {{ t('background_color') }}
                        <Tooltip>{{ t('default_button_bg_color') }}</Tooltip>
                      </label>
                    </div>
  
                    <!-- Background Hover Color -->
                    <div class="flex items-center gap-2 sm:gap-3">
                      <label class="relative cursor-pointer flex-shrink-0">
                        <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-4 border-white shadow-md ring-1 ring-gray-200" :style="{background: settings.bg_hover_color}"></div>
                        <input type="color" v-model="settings.bg_hover_color" class="absolute w-0 h-0 opacity-0" />
                      </label>
                      <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                        {{ t('background_hover_color') }}
                        <Tooltip>{{ t('button_bg_hover_tooltip') }}</Tooltip>
                      </label>
                    </div>
  
                    <!-- Text Color -->
                    <div class="flex items-center gap-2 sm:gap-3">
                      <label class="relative cursor-pointer flex-shrink-0">
                        <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-4 border-white shadow-md ring-1 ring-gray-200" :style="{background: settings.text_color}"></div>
                        <input type="color" v-model="settings.text_color" class="absolute w-0 h-0 opacity-0" />
                      </label>
                      <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                        {{ t('text_color') }}
                        <Tooltip>{{ t('button_text_color_tooltip') }}</Tooltip>
                      </label>
                    </div>
  
                    <!-- Text Hover Color -->
                    <div class="flex items-center gap-2 sm:gap-3">
                      <label class="relative cursor-pointer flex-shrink-0">
                        <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-4 border-white shadow-md ring-1 ring-gray-200" :style="{background: settings.text_hover_color}"></div>
                        <input type="color" v-model="settings.text_hover_color" class="absolute w-0 h-0 opacity-0" />
                      </label>
                      <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                        {{ t('text_hover_color') }}
                        <Tooltip>{{ t('button_text_hover_tooltip') }}</Tooltip>
                      </label>
                    </div>
                  </div>
                </div>
  
                <!-- Border Radius -->
                <div class="flex items-center gap-6 pt-4 py-2">
                  <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                    {{ t('button_border_radius') }}
                    <ProBadge />
                  </label>
                  <div class="flex items-center gap-2" :class="{ 'formychat-locked opacity-60 pointer-events-none': isLocked }">
                    <input
                      type="number"
                      v-model.number="settings.border_radius"
                      min="0"
                      max="50"
                      class="w-16 px-2 sm:px-3 h-8 sm:h-6 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                    />
                    <span class="text-xs sm:text-sm text-gray-500">px</span>
                  </div>
                </div>
  
                <!-- Open WhatsApp in new tab -->
                <div class="flex items-center gap-3 py-2">
                  <div :class="{ 'formychat-locked opacity-60 pointer-events-none': isLocked }">
                    <Toggle v-model="settings.open_new_tab" @click.stop />
                  </div>
                  <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                    {{ t('open_whatsapp_new_tab') }}
                    <ProBadge />
                  </label>
                </div>
  
                <!-- Hide Add to Cart button -->
                <div class="flex items-center gap-3 py-2">
                  <div :class="{ 'formychat-locked opacity-60 pointer-events-none': isLocked }">
                    <Toggle v-model="settings.hide_add_to_cart" @click.stop />
                  </div>
                  <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                    {{ t('hide_add_to_cart') }}
                    <ProBadge />
                  </label>
                </div>
  
                <!-- Display button on -->
                <div class="space-y-2 sm:space-y-3 py-2">
                  <label class="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-2">
                    {{ t('display_button_on') }}
                    <ProBadge />
                  </label>
                  <div class="flex flex-wrap gap-3 sm:gap-4" :class="{ 'formychat-locked opacity-60 pointer-events-none': isLocked }">
                    <button
                      @click="settings.display_desktop = !settings.display_desktop"
                      :class="[
                        'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors',
                        settings.display_desktop
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      ]"
                    >
                      <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M0 1a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm1 0v8h14V1zm5.5 11a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5M5 14a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1H5z"/>
                      </svg>
                      {{ t('desktop') }}
                    </button>
                    <button
                      @click="settings.display_mobile = !settings.display_mobile"
                      :class="[
                        'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors',
                        settings.display_mobile
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      ]"
                    >
                      <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
                        <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2"/>
                      </svg>
                      {{ t('mobile') }}
                    </button>
                  </div>
                </div>
  
              </div>
  
              <!-- Spacing before Less button -->
              <div class="pt-4 sm:pt-6"></div>
  
            </div>
          </transition>
  
            <!-- Advance Button (Show/Hide) -->
         <div>
          <button
            @click="showAdvanced = !showAdvanced"
            class="flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            <span>{{ showAdvanced ? t('less') : t('advance') }}</span>
            <svg
              class="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform"
              :class="{ 'rotate-180': showAdvanced }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
         </div>
          </template>
  
         <!-- Save/Discard Changes Buttons -->
         <transition name="slide-fade">
           <div v-if="hasChanges" class="pt-4 sm:pt-6 border-t border-gray-200 flex gap-3">
             <Button
               @click="discardChanges"
               color="secondary"
               outline
               class="flex items-center justify-center gap-2 text-sm sm:text-base"
             >
               <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
               </svg>
               <span>{{ t('discard') }}</span>
             </Button>
             <Button
               @click="saveSettings"
               color="primary"
               class="flex-1 flex items-center justify-center gap-2 text-sm sm:text-base"
             >
               <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
               </svg>
               <span>{{ t('save_changes') }}</span>
             </Button>
           </div>
         </transition>
  
        </div>
  
        <!-- Right Column - Preview (Desktop: fixed, Mobile: bottom) -->
        <div class="mt-6 xl-custom:mt-0 xl-custom:fixed xl-custom:right-8 xl-custom:top-1/2 xl-custom:-translate-y-1/2 xl-custom:w-96">
          <ProductPagePreview :settings="settings" />
        </div>
  
      </div>
  </div>
  </template>
  
  <script setup>
  import { ref, computed } from 'vue';
  import Toggle from '@components/Toggle.vue';
  import Text from '@components/Text.vue';
  import Select from '@components/Select.vue';
  import { useTranslation } from '../../composables/useTranslation';
  import Button from '@components/Button.vue';
  import Tooltip from '@components/Tooltip.vue';
  import MessagePreset from '@components/MessagePreset.vue';
  import ProBadge from '@components/ProBadge.vue';
  import ProductPagePreview from './components/ProductPagePreview.vue';
  import { useToast } from '@store/toast';
  import { useCountries } from '@store/countries';
  import { useModal } from '@store/modal';
  import { useUpgrade } from '@store/Upgrade';
  
  const toast = useToast();
  const { t } = useTranslation();
  const countriesStore = useCountries();
  const modal = useModal();
  const $upgrade = useUpgrade();
  const isLocked = computed(() => !formychat_admin_vars.is_premium);
  const whatsappError = ref(false);
  const whatsappNumberField = ref(null);
  const whatsappInput = ref(null);
  const pageTop = ref(null);
  
  // Countries from store
  const countries = computed(() => countriesStore.countries);
  
  // Default advanced settings
  const defaultAdvancedSettings = {
    bg_color: '#25D366',
    bg_hover_color: '#21bd5b',
    text_color: '#ffffff',
    text_hover_color: '#ffffff',
    border_radius: 4,
    open_new_tab: false,
    hide_add_to_cart: false,
    display_desktop: true,
    display_mobile: true
  };
  
  // Check if colors are changed from default
  const hasColorChanges = computed(() => {
    return settings.value.bg_color !== defaultAdvancedSettings.bg_color ||
      settings.value.bg_hover_color !== defaultAdvancedSettings.bg_hover_color ||
      settings.value.text_color !== defaultAdvancedSettings.text_color ||
      settings.value.text_hover_color !== defaultAdvancedSettings.text_hover_color;
  });
  
  // Reset colors to default with confirmation
  const confirmResetColors = async () => {
    const result = await modal.fire({
      title: t('reset_colors'),
      message: t('reset_colors_confirmation'),
      ok: t('reset'),
      cancel: t('cancel'),
      okColor: 'danger',
      cancelColor: 'secondary',
    });
  
    if (result.ok) {
      settings.value.bg_color = defaultAdvancedSettings.bg_color;
      settings.value.bg_hover_color = defaultAdvancedSettings.bg_hover_color;
      settings.value.text_color = defaultAdvancedSettings.text_color;
      settings.value.text_hover_color = defaultAdvancedSettings.text_hover_color;
    }
  };
  
  // Check if any advanced setting is customized
  const hasCustomizedAdvanced = () => {
    const current = window.formychat_admin_vars?.woocommerce?.product_settings;
    if (!current) return false;
  
    return Object.keys(defaultAdvancedSettings).some(key => {
      return current[key] !== undefined && current[key] !== defaultAdvancedSettings[key];
    });
  };
  
  const showAdvanced = ref(hasCustomizedAdvanced());
  
  // Phone validation function (matches widget validation)
  const isValidPhone = (phone = '') => {
    return phone?.match(/^\d{7,15}$/);
  };
  
  // Computed property for error state
  const hasWhatsAppError = computed(() => {
    if (!settings.value.enabled) return false;
    return whatsappError.value || !isValidPhone(settings.value.whatsapp_number);
  });
  
  // Reset error on keydown
  const resetWhatsAppError = () => {
    whatsappError.value = false;
  };
  
  // WooCommerce-specific variables for message template (from backend)
  const woocommerceFields = window.formychat_admin_vars?.woocommerce?.product_fields || {};
  
  // Default message template for reset
  const defaultMessageTemplate = `Hello! I'd like to order {product_name} (SKU: {product_sku}) on {site_title}.`;
  
  // Initialize settings from window object
  const getInitialSettings = () => window.formychat_admin_vars?.woocommerce?.product_settings || {
    enabled: false,
    country_code: window.formychat_country_code || '44',
    whatsapp_number: '',
    button_position: 'below_add_to_cart',
    button_text: t('buy_on_whatsapp'),
    message_template: defaultMessageTemplate,
    bg_color: '#25D366',
    bg_hover_color: '#21bd5b',
    text_color: '#ffffff',
    text_hover_color: '#ffffff',
    border_radius: 4,
    open_new_tab: false,
    hide_add_to_cart: false,
    display_desktop: true,
    display_mobile: true
  };
  
  const settings = ref(getInitialSettings());
  const savedSettings = ref(JSON.stringify(getInitialSettings()));
  
  // Check if settings have changed
  const hasChanges = computed(() => {
    return JSON.stringify(settings.value) !== savedSettings.value;
  });
  
  const positionOptions = computed(() => [
    { value: 'above_add_to_cart', label: t('above_add_to_cart') },
    { value: 'below_add_to_cart', label: t('below_add_to_cart'), locked: isLocked.value },
    { value: 'after_product_meta', label: t('after_product_meta'), locked: isLocked.value },
    { value: 'after_short_description', label: t('after_short_description'), locked: isLocked.value },
  ]);
  
  const discardChanges = () => {
    settings.value = JSON.parse(savedSettings.value);
  };
  
  const saveSettings = async () => {
    // Reset error state
    whatsappError.value = false;
  
    // Validate WhatsApp number if button is enabled
    if (settings.value.enabled && !isValidPhone(settings.value.whatsapp_number)) {
      whatsappError.value = true;
  
      // Scroll to the field smoothly
      if (whatsappNumberField.value) {
        whatsappNumberField.value.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
  
      // Focus the input field after a short delay (to allow scroll to complete)
      setTimeout(() => {
        if (whatsappInput.value && whatsappInput.value.$el) {
          const inputElement = whatsappInput.value.$el.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }
      }, 500);
  
      return;
    }
  
    try {
      const response = await fetch('/wp-json/formychat/woocommerce/product-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window.formychat_admin_vars?.rest_nonce || ''
        },
        body: JSON.stringify({
          settings: settings.value
        })
      });
  
      const data = await response.json();
  
      if (data.success) {
        toast.success(t('settings_saved_success'));
        savedSettings.value = JSON.stringify(settings.value);
  
        // Scroll to top smoothly
        if (pageTop.value) {
          pageTop.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      } else {
        toast.error(data.message || t('failed_to_save_settings'));
      }
    } catch (error) {
      toast.error(t('error_save'));
    }
  };
  
  </script>
  
  <style scoped>
  .slide-fade-enter-active {
    transition: all 0.3s ease-out;
  }
  
  .slide-fade-leave-active {
    transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
  }
  
  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
  }
  
  /* Bounce animation for validation error icon */
  .bounce-enter-active {
    animation: bounce-in 0.5s;
  }
  
  @keyframes bounce-in {
    0% {
      transform: scale(0);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }
  </style>